import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, HelpCircle, Layers, LogOut, Menu, RotateCcw } from 'lucide-react';
import { ThemeToggle } from '@/components/theme-toggle';
import { UserAvatar } from '@/components/ui/user-avatar';
import { EffetaLogo } from '@/components/ui/icons/effeta-logo';
import { HelpButton, HelpMenuItems } from '@/components/HelpButton';
import { AreaMenuItems, AreaSelector } from '@/components/AreaSelector';
import { useSessionStore } from '@/stores/session';
import { useThemeStore } from '@/stores/theme';
import { useIsPhone } from '@/hooks/use-is-phone';
import { useAreaSelection } from '@/hooks/use-area-selection';
import { useRouteHelp } from '@/hooks/use-route-help';
import { useHomeTarget } from '@/hooks/use-home-target';
import { resetTourSeen, setTourPreferences } from '@/features/auth/api';
import { useLogout } from '@/features/auth/hooks';
import { isTheme, THEME_ICON, THEME_LABEL, THEMES } from '@/lib/theme-options';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { EffetaAppsMenu } from './EffetaAppsMenu';

interface NavbarProps {
  // Callback para abrir el sidebar mobile (drawer). Solo se usa en mobile.
  onOpenMobileSidebar?: () => void;
}

// Barra superior, como la de Effetá Apps: a lo ancho de la pantalla, fija
// arriba, en `bar` (el azul de la marca en claro, oscura en oscuro). A la
// izquierda el logo blanco, el menú de apps de la suite y "| PIM", igual que
// Effetá Apps muestra "| Apps"; a la derecha área, tema, ayuda y el menú de
// usuario. Las migas van arriba del contenido (`AppShell`).
//
// En el celular (debajo de `sm`) los controles no entran en ~375 px. Ahí la
// barra queda en hamburguesa + logo + menú de apps (izq) y avatar (der), con
// área táctil de 44 px; la raya y "PIM" se ocultan, y área, tema y ayuda
// pasan al menú del avatar.
//
// La altura (`h-16`, 4rem) la descuentan `AppShell` (menú lateral), el
// Estudio de fotos y la página de integradores: si cambia, cambiar allá. El
// borde de abajo del modo oscuro va dentro de esos 4rem.
export const Navbar = ({ onOpenMobileSidebar }: NavbarProps) => {
  const isPhone = useIsPhone();
  const user = useSessionStore((s) => s.user);
  const updateTourState = useSessionStore((s) => s.updateTourState);
  const logoutMutation = useLogout();
  // Sin tarjetas en el dashboard, `/` redirige a la primera sección: el logo
  // va directo ahí y se ahorra el salto.
  const home = useHomeTarget();
  // Loading inline para las dos acciones de tours: deshabilita los ítems
  // mientras corre el PATCH/DELETE así no se dispara doble.
  const [isMutatingTours, setIsMutatingTours] = useState(false);

  // Cierra solo la sesión del PIM (la de Effetá Apps sigue abierta) y muestra
  // "Saliste del PIM". Ver `useLogout`.
  const handleLogout = (): void => {
    logoutMutation.mutate();
  };

  const neverShow = user?.tourState?.neverShow === true;

  // Toggle "No mostrarme tours". Optimista: el store se actualiza primero,
  // si el PATCH falla revertimos para no dejar al user pensando que aplicó.
  const handleToggleNeverShow = async (): Promise<void> => {
    if (isMutatingTours) return;
    const next = !neverShow;
    setIsMutatingTours(true);
    updateTourState({ neverShow: next });
    try {
      await setTourPreferences({ neverShow: next });
    } catch (err) {
      updateTourState({ neverShow: !next });
      toast.apiError('No se pudo actualizar la preferencia de tours', err);
    } finally {
      setIsMutatingTours(false);
    }
  };

  // Botón "Volver a ver todos los tours". Vacía `seen` localmente y en back.
  // Si falla, restauramos el snapshot anterior.
  const handleResetTours = async (): Promise<void> => {
    if (isMutatingTours) return;
    const prevSeen = user?.tourState?.seen ?? {};
    setIsMutatingTours(true);
    updateTourState({ seen: {} });
    try {
      await resetTourSeen();
      toast.success('Los tours volverán a aparecer.');
    } catch (err) {
      updateTourState({ seen: prevSeen });
      toast.apiError('No se pudo restablecer los tours', err);
    } finally {
      setIsMutatingTours(false);
    }
  };

  const fullName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email
    : '';

  return (
    <header className="bg-bar text-bar-fg dark:border-border sticky top-0 z-30 h-16 shadow-xs dark:border-b">
      <div className="flex h-full items-center justify-between gap-4 px-4 md:px-6">
        <div className="flex min-w-0 items-center gap-3 sm:gap-4">
          {/* Botón hamburguesa visible solo en mobile */}
          {onOpenMobileSidebar ? (
            <button
              type="button"
              onClick={onOpenMobileSidebar}
              aria-label="Abrir menú de navegación"
              className={cn(
                'text-bar-fg hover:bg-bar-hover focus-visible:outline-bar-fg inline-flex items-center justify-center rounded-md transition-colors md:hidden',
                isPhone ? 'h-11 w-11' : 'h-9 w-9',
              )}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          ) : null}
          {/* El texto del logo usa `currentColor`: blanco sobre la barra. La
              tilde y el punto quedan en el rojo de la marca. */}
          <Link
            to={home.path}
            aria-label={home.hasDashboard ? 'PIM, ir al inicio' : `PIM, ir a ${home.label}`}
            className={cn(
              'focus-visible:outline-bar-fg inline-flex shrink-0 items-center',
              isPhone && 'h-11',
            )}
          >
            <EffetaLogo className="h-7 w-auto" aria-hidden="true" />
          </Link>
          <EffetaAppsMenu isPhone={isPhone} />
          <span className="bg-bar-fg/30 hidden h-7 w-px sm:inline" aria-hidden="true" />
          <span className="hidden text-lg font-medium sm:inline">PIM</span>
        </div>

        <div className="flex items-center gap-2">
          {isPhone ? (
            // En el celular el área se elige desde el menú del avatar. El
            // selector queda montado pero oculto: su efecto es el que descarta
            // un área guardada que ya no existe, y tiene que correr siempre.
            <div hidden>
              <AreaSelector />
            </div>
          ) : (
            <>
              <AreaSelector />
              <ThemeToggle />
              <HelpButton />
            </>
          )}

          {user ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  aria-label="Abrir menú de usuario"
                  data-tour="navbar-user"
                  className={cn(
                    'focus-visible:outline-bar-fg inline-flex items-center justify-center rounded-full',
                    isPhone && 'h-11 w-11',
                  )}
                >
                  <UserAvatar user={user} size="sm" showTooltip />
                </button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  sideOffset={8}
                  className={cn(
                    'border-border bg-surface text-text z-50 min-w-56 overflow-hidden rounded-md border p-1 shadow-md',
                    'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                  )}
                >
                  {/* Cabecera del dropdown con datos del usuario */}
                  <DropdownMenu.Label className="flex flex-col gap-0.5 px-3 py-2">
                    <span className="truncate text-sm font-medium">{fullName}</span>
                    <span className="text-text-muted truncate text-xs">{user.email}</span>
                    {user.roleSlug ? (
                      <span className="bg-surface-2 text-text-muted mt-1 inline-flex w-fit items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase">
                        {user.roleSlug}
                      </span>
                    ) : null}
                  </DropdownMenu.Label>
                  <DropdownMenu.Separator className="bg-border my-1 h-px" />
                  {isPhone ? (
                    <>
                      <PhoneSettingsItems />
                      <DropdownMenu.Separator className="bg-border my-1 h-px" />
                    </>
                  ) : null}
                  {/* Preferencias de tours: toggle "no mostrar más" + reset.
                    Usamos `onSelect={e => e.preventDefault()}` para que el
                    menú no se cierre al click — el user puede hacer ambas
                    acciones sin reabrir. */}
                  <DropdownMenu.CheckboxItem
                    checked={neverShow}
                    onCheckedChange={() => {
                      void handleToggleNeverShow();
                    }}
                    onSelect={(e) => e.preventDefault()}
                    disabled={isMutatingTours}
                    className="data-highlighted:bg-surface-2 flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-hidden transition-colors select-none data-disabled:cursor-not-allowed data-disabled:opacity-60"
                  >
                    <span
                      aria-hidden="true"
                      className="border-border flex h-4 w-4 items-center justify-center rounded-sm border"
                    >
                      <DropdownMenu.ItemIndicator>
                        <Check className="h-3 w-3" />
                      </DropdownMenu.ItemIndicator>
                    </span>
                    No mostrarme tours
                  </DropdownMenu.CheckboxItem>
                  <DropdownMenu.Item
                    onSelect={(e) => {
                      e.preventDefault();
                      void handleResetTours();
                    }}
                    disabled={isMutatingTours}
                    className="data-highlighted:bg-surface-2 flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-hidden transition-colors select-none data-disabled:cursor-not-allowed data-disabled:opacity-60"
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" />
                    Volver a ver todos los tours
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator className="bg-border my-1 h-px" />
                  <DropdownMenu.Item
                    onSelect={handleLogout}
                    disabled={logoutMutation.isPending}
                    className="text-danger data-highlighted:bg-surface-2 flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-hidden transition-colors select-none data-disabled:cursor-not-allowed data-disabled:opacity-60"
                  >
                    <LogOut className="h-4 w-4" aria-hidden="true" />
                    Salir del PIM
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : null}
        </div>
      </div>
    </header>
  );
};

const PHONE_SUB_TRIGGER_CLASS =
  'flex cursor-pointer select-none items-center gap-2 rounded-sm px-3 py-2 text-sm outline-hidden transition-colors data-highlighted:bg-surface-2 data-[state=open]:bg-surface-2 data-disabled:cursor-not-allowed data-disabled:opacity-60';

// En el celular no queda lugar al costado del menú para un submenú: Radix lo
// abriría a la derecha, fuera de la pantalla, o lo daría vuelta hacia la
// izquierda, también cortado. Se abre ENCIMA del menú: `avoidCollisions`
// apagado lo deja anclado al borde derecho de la fila que lo abrió, y
// `-translate-x-full` con el ancho de esa fila lo corre sobre ella.
// `alignOffset` descuenta borde + padding para que la primera opción quede a
// la altura de la fila. El alto se limita a lo que queda de pantalla y el
// resto scrollea. Para volver sin elegir alcanza con tocar la cabecera del
// menú, que queda a la vista.
const PHONE_SUB_CONTENT_PROPS = {
  avoidCollisions: false,
  alignOffset: -5,
  className: cn(
    'z-50 max-h-(--radix-dropdown-menu-content-available-height) w-(--radix-dropdown-menu-trigger-width) -translate-x-full overflow-y-auto rounded-md border border-border bg-surface p-1 text-text shadow-md',
    'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
  ),
};

// Área, tema y ayuda dentro del menú del avatar, solo en el celular (en la
// barra no entran). Cada uno abre un submenú con las mismas opciones que su
// control de escritorio: misma lista, mismas reglas, mismo store.
const PhoneSettingsItems = () => {
  const area = useAreaSelection();
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const help = useRouteHelp();
  const ThemeIcon = THEME_ICON[theme];

  return (
    <>
      {area.isAvailable ? (
        <DropdownMenu.Sub>
          <DropdownMenu.SubTrigger disabled={area.isLoading} className={PHONE_SUB_TRIGGER_CLASS}>
            <Layers className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Área</span>
            <span className="text-text-muted ml-auto min-w-0 truncate text-xs">
              {area.activeName}
            </span>
            <ChevronRight className="text-text-muted h-4 w-4 shrink-0" aria-hidden="true" />
          </DropdownMenu.SubTrigger>
          <DropdownMenu.Portal>
            <DropdownMenu.SubContent {...PHONE_SUB_CONTENT_PROPS}>
              <AreaMenuItems
                areas={area.areas}
                activeSlug={area.activeSlug}
                onSelect={area.setActiveArea}
              />
            </DropdownMenu.SubContent>
          </DropdownMenu.Portal>
        </DropdownMenu.Sub>
      ) : null}

      {/* Elegir el tema directo: en el celular un botón que cicla obliga a
          adivinar cuántos toques faltan. */}
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger className={PHONE_SUB_TRIGGER_CLASS}>
          <ThemeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Tema</span>
          <span className="text-text-muted ml-auto min-w-0 truncate text-xs">
            {THEME_LABEL[theme]}
          </span>
          <ChevronRight className="text-text-muted h-4 w-4 shrink-0" aria-hidden="true" />
        </DropdownMenu.SubTrigger>
        <DropdownMenu.Portal>
          <DropdownMenu.SubContent {...PHONE_SUB_CONTENT_PROPS}>
            <DropdownMenu.RadioGroup
              value={theme}
              onValueChange={(value) => {
                if (isTheme(value)) setTheme(value);
              }}
            >
              {THEMES.map((option) => {
                const OptionIcon = THEME_ICON[option];
                return (
                  <DropdownMenu.RadioItem
                    key={option}
                    value={option}
                    className="data-highlighted:bg-surface-2 flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm outline-hidden transition-colors select-none"
                  >
                    <OptionIcon className="h-4 w-4" aria-hidden="true" />
                    <span className="flex-1">{THEME_LABEL[option]}</span>
                    <DropdownMenu.ItemIndicator>
                      <Check className="text-primary h-4 w-4" aria-hidden="true" />
                    </DropdownMenu.ItemIndicator>
                  </DropdownMenu.RadioItem>
                );
              })}
            </DropdownMenu.RadioGroup>
          </DropdownMenu.SubContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Sub>

      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger className={PHONE_SUB_TRIGGER_CLASS}>
          <HelpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Ayuda de esta página</span>
          <ChevronRight className="text-text-muted ml-auto h-4 w-4 shrink-0" aria-hidden="true" />
        </DropdownMenu.SubTrigger>
        <DropdownMenu.Portal>
          <DropdownMenu.SubContent {...PHONE_SUB_CONTENT_PROPS}>
            <HelpMenuItems help={help} />
          </DropdownMenu.SubContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Sub>
    </>
  );
};
